/* Muro y Papel — Craft 12.0.0 / Easify quotation, 2026-10-09.
 * Easify owns the formula. Read its rendered amount; never reimplement it.
 * No cart, checkout, order or message is submitted by this file.
 */
(() => {
  'use strict';
  const controllers = new WeakMap();
  const DIMENSION = 'input[data-tpo-dimension]';
  const AMOUNT = '.tpo_total-additional-price';
  const text = (el) => (el?.textContent || '').trim();
  const number = (value) => {
    const s = String(value ?? '').trim().replace(',', '.');
    return /^\d+(?:\.\d+)?$/.test(s) ? Number(s) : NaN;
  };

  function moneyNumber(value) {
    const match = String(value).match(/-?\d[\d.,\s\u00a0]*/);
    if (!match) return NaN;
    let s = match[0].replace(/\s/g, '');
    const last = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (last >= 0) {
      const tail = s.length - last - 1;
      if (tail === 1 || tail === 2) {
        s = s.slice(0, last).replace(/[.,]/g, '') + '.' + s.slice(last + 1);
      } else {
        s = s.replace(/[.,]/g, '');
      }
    }
    const result = Number(s);
    return Number.isFinite(result) && result >= 0 ? result : NaN;
  }

  function visible(el) {
    if (!el || el.type === 'hidden') return false;
    for (let node = el; node instanceof Element; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden') return false;
    }
    return true;
  }

  function propertyName(field) {
    return (field.name || '').replace(/^properties\[/, '').replace(/\]$/, '').trim();
  }

  class QuoteController {
    constructor(host) {
      this.host = host;
      this.form = host.querySelector('form[data-myp-quote-form]');
      this.config = JSON.parse(host.querySelector('[data-myp-quote-config]').getAttribute('data-config'));
      this.button = host.querySelector('[data-myp-quote-button]');
      this.total = host.querySelector('[data-myp-total]');
      this.error = host.querySelector('[data-myp-error]');
      this.quietUntil = 0;
      this.signature = null;
      this.amountText = null;
      this.startedAt = Date.now();
      this.onInput = this.onInput.bind(this);
      this.form.addEventListener('input', this.onInput, true);
      this.form.addEventListener('change', this.onInput, true);
      this.form.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input:not([type="submit"]):not([type="button"])')) {
          event.preventDefault();
          event.stopPropagation();
          this.quote();
        }
      });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.host, { childList: true, subtree: true, characterData: true,
        attributes: true, attributeFilter: ['value', 'disabled', 'data-table-price-valid'] });
      this.refresh();
      // A failed or disabled Easify embed must never produce an incomplete quotation.
      this.loadTimer = setTimeout(() => this.refresh(), 12000);
    }

    fields() { return Array.from(this.form.elements).filter((field) => !field.disabled); }
    dimensions() { return this.fields().filter((field) => field.matches(DIMENSION) && visible(field)); }

    onInput(event) {
      if (event.target.matches(DIMENSION) && /^\d+,\d*$/.test(event.target.value.trim())) {
        event.target.value = event.target.value.replace(',', '.');
      }
      this.quietUntil = Date.now() + 650;
      this.setError('');
      this.refresh();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.refresh(), 25);
    }

    setError(message, field) {
      if (text(this.error) !== message) this.error.textContent = message;
      this.error.hidden = !message;
      if (field) field.focus();
    }

    format(value) {
      const currency = this.config.currency || 'CLP';
      return new Intl.NumberFormat('es-CL', { style: 'currency', currency }).format(value) + ' ' + currency;
    }

    snapshot() {
      const fields = this.fields();
      const dims = this.dimensions();
      const variantId = String(this.form.querySelector('[name="id"]')?.value || '');
      const variant = this.config.variants.find((entry) => entry.id === variantId);
      const anchor = this.form.querySelector('[name="add"]');
      const unavailable = text(anchor) === window.variantStrings?.unavailable;
      const source = this.host.querySelector(AMOUNT);
      source?.closest('.tpo_total-additional-price-wrapper')?.setAttribute('aria-hidden', 'true');
      dims.forEach((field) => { field.inputMode = 'decimal'; });
      const rawAmount = text(source);
      const appAmount = moneyNumber(rawAmount);
      const appRequired = this.config.requireDimensions === true;
      const appPresent = !!this.host.querySelector('.tpo_has-option-set');
      const quantityField = fields.find((field) => field.name === 'quantity' && visible(field));
      const quantity = quantityField ? number(quantityField.value) : 1;
      let issue = '';
      let invalid = null;
      if (!variant || unavailable) issue = 'Selecciona una opción de producto válida.';
      else if (appRequired && (!dims.length || !source)) {
        issue = Date.now() - this.startedAt < 12000
          ? 'Cargando las opciones de medidas…'
          : 'No se pudo cargar el cálculo. Recarga la página para volver a intentarlo.';
      } else {
        invalid = dims.find((field) => {
          const value = number(field.value);
          const min = field.getAttribute('min');
          const max = field.getAttribute('max');
          return !Number.isFinite(value) || value <= 0 ||
            (min !== null && min !== '' && value < Number(min)) ||
            (max !== null && max !== '' && value > Number(max));
        });
        if (invalid) issue = 'Ingresa un alto y un ancho válidos, mayores que cero, en las unidades indicadas.';
      }
      if (!issue && (!Number.isInteger(quantity) || quantity < 1)) issue = 'Ingresa una cantidad válida.';
      if (!issue && this.host.querySelector('.tpo_dimension_price_input[data-table-price-valid="0"]')) {
        issue = 'Las medidas están fuera del rango permitido. Revísalas para continuar.';
      }
      const appError = Array.from(this.host.querySelectorAll('.tpo_error-message')).find((el) => visible(el) && text(el));
      if (!issue && appError) issue = text(appError);

      const baseAmount = Number(variant?.price || 0) / 100;
      let amount = baseAmount;
      if (source) {
        if (!Number.isFinite(appAmount)) issue ||= 'Espera a que se actualice el valor calculado.';
        amount = appAmount + (this.config.easifyPriceMode === 'total' ? 0 : baseAmount);
      } else if (appRequired || appPresent) {
        issue ||= 'Espera a que se cargue el valor calculado.';
      }
      if (!issue && (!Number.isFinite(amount) || amount <= 0)) issue = 'Completa las opciones para calcular el valor.';

      // Capture current input values, not HTML value attributes or cached form data.
      const signature = JSON.stringify(fields.filter((field) => field.type !== 'button' && field.type !== 'submit')
        .map((field) => [field.name, field.value, field.checked]));
      return { fields, dims, variant, quantity, amount, issue, invalid, signature, rawAmount, source };
    }

    refresh() {
      if (!this.host.isConnected) {
        this.observer.disconnect();
        clearTimeout(this.timer);
        clearTimeout(this.loadTimer);
        return;
      }
      const state = this.snapshot();
      if (this.signature !== null && this.signature !== state.signature) {
        this.quietUntil = Math.max(this.quietUntil, Date.now() + 650);
      }
      if (this.amountText !== state.rawAmount) {
        this.quietUntil = Math.max(this.quietUntil, Date.now() + 200);
      }
      this.signature = state.signature;
      this.amountText = state.rawAmount;
      const busy = Date.now() < this.quietUntil;
      const summary = state.issue
        ? (state.invalid ? 'Ingresa las medidas para calcular' : state.issue)
        : busy ? 'Actualizando el valor…' : this.format(state.amount);
      if (text(this.total) !== summary) this.total.textContent = summary;
      // Allow clicking incomplete dimensions to give a focused validation message.
      const disabled = busy || (this.config.requireDimensions && (!state.dims.length || !state.source));
      if (this.button.disabled !== !!disabled) this.button.disabled = !!disabled;
      if (busy) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.refresh(), Math.max(30, this.quietUntil - Date.now() + 10));
      }
      return state;
    }

    quote() {
      const state = this.refresh();
      if (!state) return;
      if (state.issue) return this.setError(state.issue, state.invalid);
      if (Date.now() < this.quietUntil) return this.setError('Espera un momento a que se actualice el cálculo.');
      if (!this.form.reportValidity()) return;
      const phone = String(this.config.phone || '').replace(/\D/g, '');
      if (!/^[1-9]\d{7,14}$/.test(phone)) return this.setError('El número de contacto no está configurado correctamente.');

      const lines = ['Hola, quiero cotizar este producto:', '', `Producto: ${this.config.title}`];
      if (state.variant.title && state.variant.title !== 'Default Title') lines.push(`Opción: ${state.variant.title}`);
      if (state.variant.sku) lines.push(`Código: ${state.variant.sku}`);
      const used = new Set();
      for (const field of state.dims) {
        const label = text(field.closest('.tpo_dimension_label')?.querySelector('.form__label')) || propertyName(field);
        const unit = text(field.parentElement.querySelector('span')).replace(/[()]/g, '').trim();
        lines.push(`${label}: ${field.value.trim()}${unit ? ' ' + unit : ''}`);
        used.add(field.name);
      }
      const values = new Map();
      for (const field of state.fields) {
        if (!/^properties\[/.test(field.name) || used.has(field.name) || !visible(field)) continue;
        if (propertyName(field).startsWith('_') || ['submit', 'button', 'file'].includes(field.type)) continue;
        if (['checkbox', 'radio'].includes(field.type) && !field.checked) continue;
        const selected = field.tagName === 'SELECT'
          ? Array.from(field.selectedOptions).map((option) => option.textContent.trim())
          : [String(field.value || '').trim()];
        for (const value of selected.filter(Boolean)) {
          const name = propertyName(field);
          if (!values.has(name)) values.set(name, []);
          if (!values.get(name).includes(value)) values.get(name).push(value);
        }
      }
      values.forEach((entries, name) => lines.push(`${name}: ${entries.join(', ')}`));
      if (state.quantity !== 1) lines.push(`Cantidad solicitada: ${state.quantity}`);
      lines.push(`Valor calculado: ${this.format(state.amount)}`, '', this.config.url);
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(lines.join('\n'))}`;
      this.setError('');
      // Run directly in the trusted click, without async work, for mobile popup compatibility.
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function initialize(root = document) {
    root.querySelectorAll('.myp-quote').forEach((host) => {
      if (controllers.has(host)) return;
      try { controllers.set(host, new QuoteController(host)); }
      catch (error) {
        const message = host.querySelector('[data-myp-error]');
        if (message) { message.textContent = 'No se pudo cargar la cotización. Recarga la página.'; message.hidden = false; }
        console.error('No se pudo iniciar la cotización de Muro y Papel.', error);
      }
    });
  }

  // Capture before Easify's / Craft's add-to-cart handlers; quotation never uses them.
  window.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-myp-quote-button], .myp-purchase-anchor');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!button.matches('.myp-purchase-anchor') && !button.disabled) {
      controllers.get(button.closest('.myp-quote'))?.quote();
    }
  }, true);
  window.addEventListener('submit', (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = event.target;
    const action = new URL(form.getAttribute('action') || location.href, location.href).pathname;
    if (form.hasAttribute('data-myp-quote-form') || /\/(?:cart(?:\/add)?|checkout)\/?$/.test(action)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      controllers.get(form.closest('.myp-quote'))?.quote();
    }
  }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', () => initialize());
})();
